import React from 'react'
import styled from 'styled-components'
import {connect} from 'react-redux'
import {injectIntl} from 'react-intl'
import FormControl from '@mui/material/FormControl'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import Snackbar from '@mui/material/Snackbar'
import FormControlLabel from '@mui/material/FormControlLabel'
import Autocomplete from 'components/Autocomplete'
import NumberFields from 'components/NumberFields'
import DateTimeFormat from 'components/DateTimeFormat'
import Tab from 'components/Tab'
import DateFormula from './DateFormula'
import {periods, formulaTypes, dateTypes} from 'utils/enums/formula'
import {formulaFormatter, formulaValidator, getNumberSubstitutions, getDateTimeSubstitutions} from 'utils/utils'

const Description = styled.div`
    margin-top: 30px;
`

const Tabs = styled.div`
    display: flex;
`

const Row = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: 30px;
`

const Controls = styled.div`
    display: flex;
    flex-wrap: wrap;

    > div {
        width: 33.33%;
    }
`

const FIELD_TYPE = 4
const CUSTOM_VALUE = -1

class FormulaFieldConfig extends React.Component {
    constructor(props) {
        super(props)

        this.dateFields = this.getDateFields()
        this.numberFields = this.getNumberFields()

        this.state = {
            type: props.config.type || formulaTypes.number.code,
            isValid: true,
            template: props.config.type === formulaTypes.number.code ? formulaFormatter(props.fieldId) : '',
            decimalSeparator:  props.config.type === formulaTypes.number.code ? props.config.format.decimalSeparator : '.',
            currencyPosition:  props.config.type === formulaTypes.number.code ? props.config.format.currencyPosition : 1,
            decimalLength: props.config.type === formulaTypes.number.code ? props.config.format.decimalLength : '',
            currencySymbol: props.config.type === formulaTypes.number.code ? props.config.format.currencySymbol : '',
            periodFormat: props.config.type === formulaTypes.datetime.code ?
                props.config.format : Object.values(periods).map(item => item.code),
            from: props.config.type === formulaTypes.datetime.code ? props.config.formula.from.type : dateTypes.now.code,
            fromValue: props.config.type === formulaTypes.datetime.code ? props.config.formula.from.value : dateTypes.now.label,
            to: props.config.type === formulaTypes.datetime.code ? props.config.formula.to.type : dateTypes.now.code,
            toValue: props.config.type === formulaTypes.datetime.code ? props.config.formula.to.value : dateTypes.now.label,
            renewalFrom: props.config.type === formulaTypes.renewal.code ? props.config.formula.from.type : dateTypes.exact.code,
            renewalFromValue: props.config.type === formulaTypes.renewal.code ? props.config.formula.from.value : null,
            periodicityPath: props.config.type === formulaTypes.renewal.code && props.config.formula.periodicityPath ?
                props.config.formula.periodicityPath : -1,
            periodicity: props.config.type === formulaTypes.renewal.code ? props.config.formula.periodicity : '',
            periodicityPeriod: props.config.type === formulaTypes.renewal.code ? props.config.formula.period : periods.years.code,
            periodicityMax: props.config.type === formulaTypes.renewal.code ? props.config.formula.periodicityMax : null,
            periodShow: props.config.type === formulaTypes.renewal.code ? props.config.formula.showPeriod : true,
            resultFormat: props.config.type === formulaTypes.renewal.code && props.config.formula.showPeriod ?
                props.config.format : Object.values(periods).map(item => item.code),
            format: props.config.type === formulaTypes.renewal.code && !props.config.formula.showPeriod ?
                props.config.format.format : 'DD MMMM YYYY',
            dateType: props.config.type === formulaTypes.renewal.code && !props.config.formula.showPeriod ?
                props.config.format.type : 'dateonly'
        }
    }

    handleChange = (event) => {
        this.setState({
            [event.target.name]: event.target.value
        })
    }

    changeType = () => {
        if (this.state.type === formulaTypes.number.code) {
            this.props.changeTypeToFormula({
                type: this.state.type,
                format: {
                    decimalSeparator:  this.state.decimalSeparator,
                    currencyPosition:  this.state.currencyPosition,
                    decimalLength: this.state.decimalLength,
                    currencySymbol: this.state.currencySymbol
                },
                formula: {
                    expression: this.state.template
                }
            })
        }

        if (this.state.type === formulaTypes.datetime.code) {
            this.props.changeTypeToFormula({
                type: this.state.type,
                format: this.state.periodFormat,
                formula: {
                    from: {
                        type: this.state.from,
                        value: this.state.fromValue
                    },
                    to: {
                        type: this.state.to,
                        value: this.state.toValue
                    }
                }
            })
        }

        if (this.state.type === formulaTypes.renewal.code) {
            this.props.changeTypeToFormula({
                type: this.state.type,
                format: this.state.periodShow ? this.state.resultFormat : {
                    type: this.state.dateType,
                    format: this.state.format,
                    timezone: null,
                    mindate: null,
                    maxdate: null
                },
                formula: {
                    from: {
                        type: this.state.renewalFrom,
                        value: this.state.renewalFromValue
                    },
                    period: this.state.periodicityPeriod,
                    showPeriod: this.state.periodShow,
                    periodicity: this.state.periodicity === '' ? null : Number(this.state.periodicity),
                    periodicityMax: !this.state.periodicityMax ? null : Number(this.state.periodicityMax),
                    periodicityPath: this.state.periodicityPath === CUSTOM_VALUE ? null : this.state.periodicityPath
                }
            })
        }

        this.props.onClose()
        this.props.handleCloseMenu()
    }

    getDateFields = () => {
        const fields = this.props.entity ? getDateTimeSubstitutions(this.props.entity) : []
        const options = fields.filter(item => item.id !== this.props.fieldId.toString())
        return options
    }

    getNumberFields = () => {
        const fields = this.props.entity ? getNumberSubstitutions(this.props.entity) : []
        const options = fields.filter(item => item.id !== this.props.fieldId.toString())
        return options
    }

    handleChangePeriod = (e) => {
        const periodFormat = [...this.state[e.target.name]]
        const index = periodFormat.indexOf(e.target.value)

        if (index !== -1) {
            periodFormat.splice(index, 1)

            if (!periodFormat.length) {
                periodFormat.push(periods.days.code)
            }
        } else {
            periodFormat.push(e.target.value)
        }

        this.setState({ [e.target.name]: [...periodFormat] })
    }

    handleClose = () => {
        this.setState({ isValid: true })
    }

    handleValidation = () => {
        if (this.state.type === formulaTypes.number.code) {
            const validation = formulaValidator(this.props.entity, this.state.template)

            if (validation) {
                this.changeType()
            } else {
                this.setState({ isValid: false })
            }
        } else if (this.state.type === formulaTypes.datetime.code) {
            if ((!this.state.fromValue && this.state.from === dateTypes.exact.code) ||
                (!this.state.toValue && this.state.to === dateTypes.exact.code)) {
                this.setState({ isValid: false })
            } else {
                this.changeType()
            }
        } else if (this.state.type === formulaTypes.renewal.code) {
            if ((!this.state.renewalFromValue && this.state.renewalFrom === dateTypes.exact.code) ||
                (this.state.periodicityPath === CUSTOM_VALUE && !this.state.periodicity)) {
                this.setState({ isValid: false })
            } else {
                this.changeType()
            }
        }
    }

    handleUpdate = (select, date) => {
        let type, value
        if (select === dateTypes.now.code) {
            type = dateTypes.now.code
            value = dateTypes.now.label
        } else if (select === dateTypes.exact.code) {
            type = dateTypes.exact.code
            value = null
        } else {
            type = FIELD_TYPE
            value = this.dateFields.find(item => item.id === select).pathId
        }

        this.setState({
            [date]: type,
            [date + 'Value']: value
        })
    }

    handleChangeExactDate = (select, date) => {
        this.setState({
            [date + 'Value']: select
        })
    }

    handleChangePeriodicity = (e) => {
        if (e.target.value !== CUSTOM_VALUE) {
            this.setState({
                periodicityPath: this.numberFields.find(item => item.id === e.target.value).pathId
            })
        } else {
            this.setState({
                periodicityPath: CUSTOM_VALUE
            })
        }
    }

    handleChangeFormat = (format) => {
        this.setState({ ...format })
    }

    render() {
        const { intl } = this.props

        return (
            <React.Fragment>
                <DialogTitle>
                    {intl.formatMessage({id: "config.formula_field_title"})}
                </DialogTitle>
                <DialogContent>
                    <Tabs>
                        {Object.values(formulaTypes).map((item, index) =>
                            <Tab key={index} active={this.state.type === item.code} onClick={() => this.setState({ type: item.code })}>
                                {intl.formatMessage({id: `config.formula_field_${item.code}`})}
                            </Tab>
                        )}
                    </Tabs>
                    {this.state.type === formulaTypes.number.code &&
                        <React.Fragment>
                            {this.numberFields.length ?
                                <React.Fragment>
                                    <Autocomplete
                                        style={{margin: '30px 0', height: '100px'}}
                                        value={this.state.template}
                                        label={intl.formatMessage({id: "config.formula_title"})}
                                        onChange={(value) => this.setState({template: value})}
                                        options={this.numberFields.map(field => field.name)}
                                        component='textarea'
                                    />
                                    <div>
                                        <NumberFields
                                            decimalLength={this.state.decimalLength}
                                            handleChange={this.handleChange}
                                            currencySymbol={this.state.currencySymbol}
                                            decimalSeparator={this.state.decimalSeparator}
                                            currencyPosition={this.state.currencyPosition}
                                        />
                                    </div>
                                </React.Fragment> :
                                <Description>
                                    {intl.formatMessage({id: "config.formula_no_number_field"})}
                                </Description>
                            }
                        </React.Fragment>
                    }
                    {this.state.type === formulaTypes.datetime.code &&
                        <React.Fragment>
                            {this.dateFields.length ?
                                <React.Fragment>
                                    <DateFormula
                                        date='from'
                                        value={this.state.fromValue}
                                        dateType={this.state.from}
                                        label="config.formula_from_date"
                                        fields={this.dateFields}
                                        handleUpdate={this.handleUpdate}
                                        handleChangeExactDate={this.handleChangeExactDate}
                                    />
                                    <DateFormula
                                        date='to'
                                        value={this.state.toValue}
                                        dateType={this.state.to}
                                        label="config.formula_to_date"
                                        fields={this.dateFields}
                                        handleUpdate={this.handleUpdate}
                                        handleChangeExactDate={this.handleChangeExactDate}
                                    />
                                    <Controls>
                                        {Object.values(periods).map((item, index) =>
                                            <div key={index}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={this.state.periodFormat.indexOf(item.code) !== -1}
                                                            onChange={this.handleChangePeriod}
                                                            value={item.code}
                                                            inputProps={{
                                                                name: 'periodFormat'
                                                            }}
                                                        />
                                                    }
                                                    label={intl.formatMessage({id: `config.formula_period_${item.label}`})}
                                                />
                                            </div>
                                        )}
                                    </Controls>
                                </React.Fragment> :
                                <Description>
                                    {intl.formatMessage({id: "config.formula_no_date_field"})}
                                </Description>
                            }
                        </React.Fragment>
                    }
                    {this.state.type === formulaTypes.renewal.code &&
                        <React.Fragment>
                            {this.dateFields.length ?
                                <React.Fragment>
                                    <DateFormula
                                        date='renewalFrom'
                                        value={this.state.renewalFromValue}
                                        dateType={this.state.renewalFrom}
                                        label="config.formula_from_date"
                                        fields={this.dateFields}
                                        handleUpdate={this.handleUpdate}
                                        handleChangeExactDate={this.handleChangeExactDate}
                                    />
                                    <Row>
                                        <FormControl style={{width: '245px'}} variant="standard">
                                            <InputLabel shrink={true}>{intl.formatMessage({id: "config.date_format"})}</InputLabel>
                                            <Select
                                                value={this.state.periodicityPath === CUSTOM_VALUE ?
                                                    this.state.periodicityPath : this.state.periodicityPath.join(',')}
                                                onChange={this.handleChangePeriodicity}
                                                inputProps={{
                                                    name: 'periodicityPath'
                                                }}
                                            >
                                                <MenuItem value={CUSTOM_VALUE}> Custom value </MenuItem>
                                                {this.numberFields.map((item, index) =>
                                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                        {this.state.periodicityPath === CUSTOM_VALUE &&
                                            <TextField variant="standard"
                                                type='number'
                                                style={{margin: '0 30px'}}
                                                onChange={this.handleChange}
                                                value={this.state.periodicity}
                                                inputProps={{
                                                    name: 'periodicity'
                                                }}
                                            />
                                        }
                                        <Select
                                            style={{width: '120px'}}
                                            value={this.state.periodicityPeriod}
                                            onChange={this.handleChange}
											variant="standard"
                                            inputProps={{
                                                name: 'periodicityPeriod'
                                            }}
                                        >
                                            {Object.values(periods).map((item, index) =>
                                                <MenuItem key={index} value={item.code}>
                                                    {intl.formatMessage({id: `config.formula_period_${item.label}`})}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </Row>
                                    <Row>
                                        <TextField variant="standard"
                                            style={{width: '100%'}}
                                            label={intl.formatMessage({id: "config.formula_periods_number"})}
                                            disabled={this.state.periodicityMax === null ? true : false}
                                            onChange={this.handleChange}
                                            value={this.state.periodicityMax === null ? '' : this.state.periodicityMax}
                                            type='number'
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            inputProps={{
                                                name: 'periodicityMax'
                                            }}
                                        />
                                        <FormControlLabel
                                            style={{margin: '0 0 0 20px', minWidth: '110px'}}
                                            control={
                                                <Checkbox
                                                    checked={this.state.periodicityMax === null ? true : false}
                                                    onChange={(e) => this.setState({
                                                        periodicityMax: e.target.checked ? null : ''
                                                    })}
                                                />
                                            }
                                            label={intl.formatMessage({id: "config.formula_eternal"})}
                                        />
                                    </Row>
                                    <FormControl style={{width: '100%', margin: '30px 0 20px'}} variant="standard">
                                        <InputLabel shrink={true}>{intl.formatMessage({id: "config.formula_result"})}</InputLabel>
                                        <Select
                                            value={this.state.periodShow}
                                            onChange={this.handleChange}
                                            inputProps={{
                                                name: 'periodShow'
                                            }}
                                        >
                                            <MenuItem value={true}>
                                                {intl.formatMessage({id: "config.formula_period_result"})}
                                            </MenuItem>
                                            <MenuItem value={false}>
                                                {intl.formatMessage({id: "config.formula_date_result"})}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    {this.state.periodShow ?
                                        <Controls>
                                            {Object.values(periods).map((item, index) =>
                                                <div key={index}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={this.state.resultFormat.indexOf(item.code) !== -1}
                                                                onChange={this.handleChangePeriod}
                                                                value={item.code}
                                                                inputProps={{
                                                                    name: 'resultFormat'
                                                                }}
                                                            />
                                                        }
                                                        label={intl.formatMessage({id: `config.formula_period_${item.label}`})}
                                                    />
                                                </div>
                                            )}
                                        </Controls>
                                        :
                                        <DateTimeFormat
                                            type={this.state.dateType}
                                            format={this.state.format}
                                            handleChange={this.handleChangeFormat}
                                        />
                                    }
                                </React.Fragment> :
                                <Description>
                                    {intl.formatMessage({id: "config.formula_no_date_field"})}
                                </Description>
                            }
                        </React.Fragment>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.props.onClose} color="primary">
                        {intl.formatMessage({id: "app.confirm_cancel"})}
                    </Button>
                    <Button onClick={this.handleValidation} color="primary" variant="contained">
                        {intl.formatMessage({id: "app.confirm_save"})}
                    </Button>
                </DialogActions>
                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    onClose={this.handleClose}
                    open={!this.state.isValid}
                    autoHideDuration={2000}
                    message={intl.formatMessage({id: "config.formula_invalid"})}
                />
            </React.Fragment>
        )
    }
}

const mapStateToProps = ({entities}) => ({
    entities: entities.entities
})

export default connect(mapStateToProps, {})(injectIntl(FormulaFieldConfig))
